import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { Button, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from "@material-ui/core";
import { useTheme, useMediaQuery } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import MoreVertIcon from "@material-ui/icons/MoreVert";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import TransactionModal from "../../components/TransactionModal";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useFinanceiroStyles } from "./financeiroShared";
import CaixaPanel from "./CaixaPanel";
import ContasReceberPanel from "./ContasReceberPanel";
import RelatorioPanel from "./RelatorioPanel";
import FaturasPanel from "./FaturasPanel";
import FinanceiroConfigDialog from "./FinanceiroConfigDialog";

const TABS = ["Caixa", "A Receber", "Relatórios", "Faturas"];

const Financeiro = () => {
  const classes = useFinanceiroStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { licenseExpired, user } = useContext(AuthContext);
  const location = useLocation();

  // Abre direto em "Faturas" (índice 3) quando: a licença está vencida (o usuário
  // vem para cá para pagar) ou quando a navegação pede explicitamente
  // (?tab=faturas — ex.: botão "Renovar agora" da faixa de aviso).
  const wantsInvoices = new URLSearchParams(location.search).get("tab") === "faturas";
  const [tab, setTab] = useState(licenseExpired || wantsInvoices ? 3 : 0);
  const [configOpen, setConfigOpen] = useState(false);
  const [subtitle, setSubtitle] = useState("");
  const [txModalOpen, setTxModalOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [receivableCount, setReceivableCount] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const exportRef = useRef(null);

  const registerExport = useCallback((fn) => {
    exportRef.current = fn;
  }, []);

  // Selo com a quantidade de contas a receber na aba.
  useEffect(() => {
    if (user?.profile && user.profile !== "admin") return;
    let alive = true;
    api
      .get("/transactions/accounts-receivable")
      .then(({ data }) => {
        if (!alive) return;
        const n = data.appointments?.length || 0;
        setReceivableCount(data.hasMore ? `${n}+` : n);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [tab, reloadKey, user?.profile]);

  // O menu já esconde o Financeiro de não-admins, mas a rota precisa do
  // mesmo bloqueio para impedir acesso direto pela URL.
  if (user?.profile && user.profile !== "admin") {
    return <Redirect to="/tickets" />;
  }

  const openNew = () => {
    setSelectedTx(null);
    setTxModalOpen(true);
  };
  const openEdit = (tx) => {
    setSelectedTx(tx);
    setTxModalOpen(true);
  };
  const handleExport = () => exportRef.current && exportRef.current();

  return (
    <MainContainer>
      <FinanceiroConfigDialog open={configOpen} onClose={() => setConfigOpen(false)} />
      <TransactionModal
        open={txModalOpen}
        onClose={() => {
          setTxModalOpen(false);
          setSelectedTx(null);
        }}
        transaction={selectedTx}
        onSave={() => setReloadKey((k) => k + 1)}
      />

      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>Financeiro</Title>
          {subtitle && (
            <Typography variant="body2" className={classes.muted}>
              {subtitle}
            </Typography>
          )}
        </div>

        {isMobile ? (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.headerButton}
              style={{ height: 36 }}
              startIcon={<AddIcon />}
              onClick={openNew}
            >
              Novo
            </Button>
            <IconButton aria-label="Mais opções" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {tab === 0 && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    handleExport();
                  }}
                >
                  <ListItemIcon>
                    <GetAppIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Exportar CSV" />
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setConfigOpen(true);
                }}
              >
                <ListItemIcon>
                  <SettingsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Categorias e formas de pagamento" />
              </MenuItem>
            </Menu>
          </div>
        ) : (
          <div className={classes.headerButtons}>
            <Button
              variant="outlined"
              className={`${classes.headerButton} ${classes.secondaryButton}`}
              startIcon={<SettingsOutlinedIcon />}
              onClick={() => setConfigOpen(true)}
            >
              Categorias e formas de pagamento
            </Button>
            {tab === 0 && (
              <Tooltip title="Exportar os lançamentos filtrados (CSV)">
                <Button
                  variant="outlined"
                  className={`${classes.headerButton} ${classes.secondaryButton}`}
                  startIcon={<GetAppIcon />}
                  onClick={handleExport}
                >
                  Exportar
                </Button>
              </Tooltip>
            )}
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.headerButton}
              startIcon={<AddIcon />}
              onClick={openNew}
            >
              Novo lançamento
            </Button>
          </div>
        )}
      </div>

      <div className={classes.tabs} role="tablist" aria-label="Seções do financeiro">
        {TABS.map((label, i) => (
          <Button
            key={label}
            role="tab"
            aria-selected={tab === i}
            className={`${classes.tab} ${tab === i ? classes.tabActive : ""}`}
            onClick={() => setTab(i)}
          >
            {label}
            {i === 1 && receivableCount > 0 && (
              <span className={classes.tabBadge}>{receivableCount}</span>
            )}
          </Button>
        ))}
      </div>

      {tab === 0 && (
        <CaixaPanel
          onEdit={openEdit}
          reloadKey={reloadKey}
          registerExport={registerExport}
          onSubtitle={setSubtitle}
        />
      )}
      {tab === 1 && <ContasReceberPanel onSubtitle={setSubtitle} />}
      {tab === 2 && <RelatorioPanel onSubtitle={setSubtitle} />}
      {tab === 3 && <FaturasPanel onSubtitle={setSubtitle} />}
    </MainContainer>
  );
};

export default Financeiro;
